import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import { Plus, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import ExportButton from "@/components/ExportButton";
import ProductPicker from "@/components/ProductPicker";
import DeleteButton from "@/components/DeleteButton";
import { useAuth } from "@/context/AuthContext";
import { prettyCat, buildProductName } from "@/lib/catalog";

const PRIORITIES = ["low", "medium", "high"];
const STATUSES = ["pending", "active", "done"];

const EMPTY = {
  date: new Date().toISOString().slice(0, 10),
  category: "terrazzo_tiles", variant: "", color: "", size: "", custom_size: "",
  product_name: "", unit: "pcs",
  target_quantity: "", priority: "medium", notes: "",
};

export default function Schedule() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/schedule").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.target_quantity) { toast.error("Target quantity required"); return; }
    try {
      await api.post("/schedule", {
        date: form.date,
        product_category: form.category,
        product_name: form.product_name || buildProductName(form),
        variant: form.variant || null,
        color: form.color || null,
        size: form.size || null,
        custom_size: form.custom_size || null,
        target_quantity: parseFloat(form.target_quantity),
        unit: form.unit,
        priority: form.priority,
        notes: form.notes || null,
        created_by: "",
      });
      toast.success("Scheduled");
      setOpen(false);
      setForm({ ...EMPTY, date: new Date().toISOString().slice(0, 10) });
      load();
    } catch { toast.error("Failed"); }
  };

  const setStatus = async (id, status) => {
    try { await api.patch(`/schedule/${id}`, { status }); toast.success("Updated"); load(); } catch { toast.error("Failed"); }
  };

  const priBadge = (p) => p === "high" ? "border-[#C85A42]/40 text-[#C85A42]" : p === "medium" ? "border-[#1C1C1A]/40 text-[#1C1C1A]" : "border-[#6E6E6A]/30 text-[#6E6E6A]";

  return (
    <div className="p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A]">Plan</div>
            <h1 className="font-display text-4xl tracking-tighter font-black">Manufacturing Schedule</h1>
          </div>
          <div className="flex gap-2 items-center">
            <ExportButton module="schedule" />
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="add-schedule" className="bg-[#1C1C1A] hover:bg-black text-white"><Plus className="w-4 h-4 mr-2" /> Schedule work</Button></DialogTrigger>
              <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto">
                <DialogHeader><DialogTitle>Schedule production</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-xs">Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                  <div>
                    <Label className="text-xs">Priority</Label>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="outline" className="w-full justify-between mt-2 capitalize">{form.priority}<ChevronDown className="w-4 h-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent>{PRIORITIES.map(p => <DropdownMenuItem key={p} onClick={() => setForm({ ...form, priority: p })} className="capitalize">{p}</DropdownMenuItem>)}</DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
                <div className="mt-4"><ProductPicker value={form} onChange={setForm} testidPrefix="sched" /></div>
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div><Label className="text-xs">Target quantity</Label><Input type="number" value={form.target_quantity} onChange={(e) => setForm({ ...form, target_quantity: e.target.value })} className="h-11 text-base" /></div>
                  <div><Label className="text-xs">Unit</Label><Input value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
                  <div className="col-span-2"><Label className="text-xs">Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                </div>
                <Button onClick={save} data-testid="sched-save" className="mt-4 bg-[#1C1C1A] hover:bg-black text-white h-11">Schedule</Button>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
          {STATUSES.map(s => (
            <div key={s} className="space-y-3">
              <div className="text-[10px] tracking-[0.3em] uppercase text-[#6E6E6A] pl-1">{s}</div>
              {rows.filter(r => r.status === s).length === 0 && (
                <div className="text-xs text-[#6E6E6A] p-3 border border-dashed border-[#D8D8D5] rounded-md">Empty</div>
              )}
              {rows.filter(r => r.status === s).map(r => (
                <Card key={r.id} className="p-4 border border-[#D8D8D5] shadow-none rounded-md">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6E6E6A]">{r.date} · {prettyCat(r.product_category)}</div>
                      <div className="font-medium text-[#1C1C1A] mt-1">{r.product_name || prettyCat(r.product_category)}</div>
                      <div className="text-sm text-[#6E6E6A] mt-1">Target: {r.target_quantity} {r.unit}</div>
                    </div>
                    <Badge variant="outline" className={priBadge(r.priority)}>{r.priority}</Badge>
                  </div>
                  <div className="flex gap-2 mt-3">
                    {STATUSES.filter(ns => ns !== r.status).map(ns => (
                      <Button key={ns} size="sm" variant="outline"
                        data-testid={`sched-move-${r.id}-${ns}`}
                        onClick={() => setStatus(r.id, ns)}
                        className="text-xs h-7">→ {ns}</Button>
                    ))}
                    {user?.role === "admin" && (
                      <DeleteButton endpoint={`/schedule/${r.id}`} label="schedule item" onDone={load} testid={`del-sched-${r.id}`} />
                    )}
                  </div>
                </Card>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
